import {
  AccountDetailGroup,
  IAccountDetailItem,
} from '@varo/graphql-schema-types';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface ManageAccountItemsProps {
  items: Array<AccountDetailGroup> | undefined;
  onPress?: (item: IAccountDetailItem) => void;
  accountType: string;
}

interface ManageAccountItemContentProps {
  item: IAccountDetailItem;
  onPress?: (item: IAccountDetailItem) => void;
  index: number;
  accountType: string;
}

const ManageAccountItemContent = ({
  item,
  onPress,
  index,
  accountType,
}: ManageAccountItemContentProps) => {
  const trackerPress = usePressableAnalytics({
    analyticsId: 'savingTrackerClick',
    customAttributes: { faq_name: 'how_to_earn_high_apy' },
  });

  const showHelpIcon =
    item.description &&
    !item.itemDetail &&
    !item.articleLink &&
    item.key !== 'interest_year_to_date';

  if (item.key === 'savings_tracker_apy') {
    return (
      <>
        {index > 0 ? (
          <View style={styles.dividerContainer}>
            <HairLineDivider />
          </View>
        ) : null}
        <View style={styles.linkWrapper}>
          <HyperlinkBaseText
            onPress={() => {
              trackerPress();
              onPress?.(item);
            }}
          >
            {item.value}
          </HyperlinkBaseText>
        </View>
      </>
    );
  }

  const getBalanceAnalyticsId = (item: IAccountDetailItem) => {
    if (item.key === 'available_balance') {
      return 'availableBalance';
    } else if (item.key === 'total_balance') {
      return 'totalBalance';
    }
    return 'currentBalance';
  };
  return (
    <>
      {index > 0 && item.key !== 'interest_month_to_date' ? (
        <View style={styles.dividerContainer}>
          <HairLineDivider />
        </View>
      ) : null}
      <ListItem
        key={item.key}
        primaryCopy={item.value}
        secondaryCopy={item.title}
        lineFormat={ListItemLineFormat.LABEL}
        showsChevron={item.key === 'apy'}
        secondaryNumberOfLines={2}
        onPress={() => {
          onPress?.(item);
        }}
        trailingAccessory={
          showHelpIcon
            ? {
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'QUESTION_MARK',
                  iconAccessibilityLabel: 'Help icon',
                },
              }
            : undefined
        }
        {...(item.key === 'apy' && {
          analyticsId: 'currentAPYClicked',
          customAttributes: {
            current_APY: item.value,
          },
        })}
        {...((item.key === 'available_balance' ||
          item.key === 'total_balance' ||
          item.key === 'current_balance') && {
          analyticsId: getBalanceAnalyticsId(item),
          customAttributes: {
            category: accountType,
          },
        })}
      />
    </>
  );
};

const ManageAccountItems = ({
  items,
  onPress,
  accountType,
}: ManageAccountItemsProps) => {
  const accountDetailMoneyItems = items?.[1];
  const accountDetailCopyGroup = items?.[0];

  const accountDetailCopyItems = accountDetailCopyGroup?.items.filter(
    (item) => item.key !== 'date_added',
  );

  const dateAddedItem = accountDetailCopyGroup?.items.filter(
    (item) => item.key === 'date_added',
  );

  if (accountType !== 'external') {
    return items?.map((item, index) => {
      return (
        // No other key available for unique id
        // eslint-disable-next-line react/no-array-index-key
        <View key={`randomKey${index}`}>
          {index > 0 ? <View style={styles.sectionDivider} /> : null}
          {item.title ? <SectionDivider labelText={item.title} /> : null}
          {item.items.map((item, i) => {
            return (
              <ManageAccountItemContent
                key={`childrenKey${item.key}`}
                item={item}
                index={i}
                onPress={onPress}
                accountType={accountType}
              />
            );
          })}
        </View>
      );
    });
  }

  return (
    <>
      {accountDetailCopyItems?.map((item, i) => {
        return (
          <ManageAccountItemContent
            key={item.key}
            item={item}
            index={i}
            onPress={onPress}
            accountType={accountType}
          />
        );
      })}
      {accountDetailCopyItems && accountDetailCopyItems?.length > 0 ? (
        <View style={styles.dividerContainer}>
          <HairLineDivider />
        </View>
      ) : null}
      {accountDetailMoneyItems?.items.map((item, i) => {
        return (
          <ManageAccountItemContent
            key={item.key}
            item={item}
            index={i}
            onPress={onPress}
            accountType={accountType}
          />
        );
      })}
      {dateAddedItem?.map((item, i) => {
        return (
          <React.Fragment key={item.key}>
            <View style={styles.divider} />
            <ManageAccountItemContent
              item={item}
              index={i}
              onPress={onPress}
              accountType={accountType}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

const styles = StyleSheet.create({
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
    marginBottom: theme.spacing.s,
  },
  dividerContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  linkWrapper: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
});

export default ManageAccountItems;
